// DOM HUD + start/pause/game-over overlays. Subscribes to the event bus;
// gameplay code never touches the DOM directly. The start screen carries a
// minimal district picker (full v1 selection carousel ports in Phase 3).

import type { EventBus } from '@/app/events';
import { DISTRICTS, type DistrictDef } from '@/content/districts';
import { POWER_UP_TYPES } from '@/content/powerups';
import { SHOP_ITEMS, type ShopItem } from '@/content/shop-items';
import type { DailyModifier } from '@/content/daily-modifiers';
import { beltAt } from '@/systems/belts';
import type { SaveV2, LeaderboardEntry } from '@/save/schema';
import { owns, canAfford } from '@/meta/shop';
import type { Lang, StringKey } from '@/ui/i18n';
import { makeTranslate } from '@/ui/i18n';

export class Hud {
  onDistrictSelect: ((district: DistrictDef) => void) | null = null;
  onMuteToggle: (() => void) | null = null;
  onOpenShop: (() => void) | null = null;
  onOpenLeaderboard: (() => void) | null = null;
  onStartDaily: (() => void) | null = null;
  onShopBuy: ((item: ShopItem) => void) | null = null;
  onShopEquip: ((item: ShopItem) => void) | null = null;
  onCloseOverlayToMenu: (() => void) | null = null;
  onSubmitInitials: ((initials: string) => void) | null = null;

  private readonly t: (key: StringKey) => string;
  private readonly scoreEl: HTMLElement;
  private readonly distanceEl: HTMLElement;
  private readonly comboEl: HTMLElement;
  private readonly districtEl: HTMLElement;
  private readonly fpsEl: HTMLElement;
  private readonly overlayEl: HTMLElement;

  constructor(root: HTMLElement, lang: Lang, bus: EventBus) {
    this.t = makeTranslate(lang);
    root.innerHTML = `
      <div class="hud-top">
        <div class="hud-stat"><span class="hud-label">${this.t('score')}</span> <span id="hud-score">0</span></div>
        <div class="hud-stat"><span class="hud-label">${this.t('distance')}</span> <span id="hud-distance">0m</span></div>
        <div class="hud-stat" id="hud-district"></div>
        <div class="hud-stat hud-combo" id="hud-combo" hidden></div>
        <div class="hud-stat hud-shield" id="hud-shield" title="Second Wind">🛡</div>
        <div class="hud-stat hud-powerup" id="hud-powerup" hidden></div>
        <div class="hud-stat hud-fps" id="hud-fps"></div>
        <button class="hud-mute" id="hud-mute" title="Music">🔊</button>
      </div>
      <div class="hud-goal" id="hud-goal"></div>
      <div class="hud-overlay" id="hud-overlay"></div>
    `;
    this.scoreEl = root.querySelector('#hud-score') as HTMLElement;
    this.distanceEl = root.querySelector('#hud-distance') as HTMLElement;
    this.comboEl = root.querySelector('#hud-combo') as HTMLElement;
    this.districtEl = root.querySelector('#hud-district') as HTMLElement;
    this.fpsEl = root.querySelector('#hud-fps') as HTMLElement;
    this.overlayEl = root.querySelector('#hud-overlay') as HTMLElement;

    bus.on('score:awarded', ({ total }) => {
      this.scoreEl.textContent = String(total);
    });
    bus.on('combo:changed', ({ combo, multiplier }) => {
      if (combo >= 3) {
        this.comboEl.hidden = false;
        this.comboEl.textContent = `${combo}x combo · ×${multiplier}`;
      } else {
        this.comboEl.hidden = true;
      }
    });
    bus.on('run:started', ({ districtId }) => {
      const district = DISTRICTS[districtId];
      this.districtEl.textContent = district ? district.name : districtId;
      this.setShield(true);
    });

    // Power-up indicator with countdown (v1 #power-up-indicator)
    const powerUpEl = root.querySelector('#hud-powerup') as HTMLElement;
    let powerUpLabel = '';
    bus.on('powerup:activated', ({ typeId }) => {
      const def = POWER_UP_TYPES[typeId];
      powerUpLabel = def.label;
      powerUpEl.hidden = false;
      powerUpEl.style.color = def.hudColor;
      powerUpEl.textContent = powerUpLabel;
    });
    bus.on('powerup:tick', ({ remaining }) => {
      if (!powerUpEl.hidden) powerUpEl.textContent = `${powerUpLabel} ${remaining.toFixed(0)}s`;
    });
    bus.on('powerup:deactivated', () => {
      powerUpEl.hidden = true;
    });

    // Shield dims once Second Wind is spent (v1 updateShieldHUD)
    bus.on('player:hit', ({ fatal }) => {
      if (!fatal) this.setShield(false);
    });

    const muteBtn = root.querySelector('#hud-mute') as HTMLButtonElement;
    muteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onMuteToggle?.();
    });
    muteBtn.addEventListener('touchend', (e) => e.stopPropagation(), { passive: true });
  }

  private setShield(available: boolean): void {
    const el = document.getElementById('hud-shield');
    if (el) el.style.opacity = available ? '1' : '0.25';
  }

  setMuted(muted: boolean): void {
    const el = document.getElementById('hud-mute');
    if (el) el.textContent = muted ? '🔇' : '🔊';
  }

  updateDistance(distance: number): void {
    this.distanceEl.textContent = `${Math.floor(distance)}m`;
  }

  setGoalText(text: string): void {
    const el = document.getElementById('hud-goal');
    if (el) el.textContent = text;
  }

  updateFps(fps: number): void {
    this.fpsEl.textContent = `${fps} fps`;
    this.fpsEl.style.color = fps >= 50 ? '#7fff7f' : fps >= 30 ? '#ffd700' : '#ff7070';
  }

  showStart(selectedDistrictId: string, wallet: number): void {
    const chips = Object.values(DISTRICTS)
      .map(
        (d) =>
          `<button class="district-chip${d.id === selectedDistrictId ? ' selected' : ''}"
                   data-district="${d.id}">${d.name}</button>`,
      )
      .join('');
    this.overlayEl.innerHTML = `
      <div class="overlay-card">
        <h1>${this.t('title')}</h1>
        <p class="wallet-line">🪙 ${wallet}</p>
        <div class="district-row">${chips}</div>
        <div class="menu-row">
          <button class="menu-btn" data-menu="daily">📅 ${this.t('daily')}</button>
          <button class="menu-btn" data-menu="shop">🥋 ${this.t('shop')}</button>
          <button class="menu-btn" data-menu="best">🏆 ${this.t('best')}</button>
        </div>
        <p>${this.t('tapToStart')}</p>
      </div>`;
    this.overlayEl.hidden = false;

    for (const btn of this.overlayEl.querySelectorAll<HTMLButtonElement>('.district-chip')) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const district = DISTRICTS[btn.dataset.district ?? ''];
        if (district && this.onDistrictSelect) this.onDistrictSelect(district);
      });
      // Keep taps on chips from bubbling into the jump/start touch handler
      btn.addEventListener('touchend', (e) => e.stopPropagation(), { passive: true });
    }
    for (const btn of this.overlayEl.querySelectorAll<HTMLButtonElement>('.menu-btn')) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const kind = btn.dataset.menu;
        if (kind === 'daily') this.onStartDaily?.();
        else if (kind === 'shop') this.onOpenShop?.();
        else if (kind === 'best') this.onOpenLeaderboard?.();
      });
      btn.addEventListener('touchend', (e) => e.stopPropagation(), { passive: true });
    }
  }

  /** Dojo shop overlay (Phase 6). Re-rendered after every purchase/equip. */
  showShop(save: SaveV2, lang: Lang): void {
    const rows = SHOP_ITEMS.map((item) => {
      const owned = owns(save, item.id);
      const equipped = item.kind !== 'modifier' && save.equipped[item.kind] === item.id;
      const swatch =
        item.color !== undefined
          ? `<span class="swatch" style="background:#${item.color.toString(16).padStart(6, '0')}"></span>`
          : '🥋';
      let action: string;
      if (equipped) action = `<span class="shop-state">${this.t('equipped')}</span>`;
      else if (owned && item.kind !== 'modifier')
        action = `<button class="shop-btn" data-equip="${item.id}">${this.t('equip')}</button>`;
      else if (owned) action = `<span class="shop-state">${this.t('owned')}</span>`;
      else
        action = `<button class="shop-btn" data-buy="${item.id}" ${canAfford(save, item) ? '' : 'disabled'}>
            ${this.t('buy')} · 🪙${item.price}</button>`;
      return `
        <div class="shop-row">
          <div class="shop-icon">${swatch}</div>
          <div class="shop-info">
            <strong>${item.name[lang]}</strong>
            <small>${item.description[lang]}</small>
          </div>
          ${action}
        </div>`;
    }).join('');

    this.overlayEl.innerHTML = `
      <div class="overlay-card shop-card">
        <h1>${this.t('shop')}</h1>
        <p class="wallet-line">🪙 ${save.wallet}</p>
        <div class="shop-list">${rows}</div>
        <button class="menu-btn" data-menu="back">← ${this.t('back')}</button>
      </div>`;
    this.overlayEl.hidden = false;
    this.wireOverlayButtons();
  }

  showLeaderboard(entries: readonly LeaderboardEntry[]): void {
    const rows =
      entries.length === 0
        ? `<p>—</p>`
        : entries
            .map(
              (e, i) => `
        <div class="board-row">
          <span class="board-rank">${i + 1}.</span>
          <strong>${e.initials}</strong>
          <span class="board-score">${e.score}</span>
          <small>${Math.floor(e.distance)}m · ${e.district} · ${e.date}</small>
        </div>`,
            )
            .join('');
    this.overlayEl.innerHTML = `
      <div class="overlay-card shop-card">
        <h1>🏆 ${this.t('best')}</h1>
        <div class="shop-list">${rows}</div>
        <button class="menu-btn" data-menu="back">← ${this.t('back')}</button>
      </div>`;
    this.overlayEl.hidden = false;
    this.wireOverlayButtons();
  }

  /** Daily intro: shows today's modifier before the seeded run starts. */
  showDailyIntro(modifier: DailyModifier, lang: Lang, bestToday: number, onGo: () => void): void {
    this.overlayEl.innerHTML = `
      <div class="overlay-card">
        <h1>📅 ${this.t('daily')}</h1>
        <p><strong>${modifier.name[lang]}</strong></p>
        <p>${modifier.description[lang]}</p>
        ${bestToday > 0 ? `<p>${this.t('todaysBest')}: <strong>${bestToday}</strong></p>` : ''}
        <button class="menu-btn menu-go" data-menu="go">${this.t('playDaily')}</button>
      </div>`;
    this.overlayEl.hidden = false;
    const go = this.overlayEl.querySelector<HTMLButtonElement>('[data-menu="go"]');
    go?.addEventListener('click', (e) => {
      e.stopPropagation();
      onGo();
    });
    go?.addEventListener('touchend', (e) => e.stopPropagation(), { passive: true });
  }

  private wireOverlayButtons(): void {
    const back = this.overlayEl.querySelector<HTMLButtonElement>('[data-menu="back"]');
    back?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onCloseOverlayToMenu?.();
    });
    back?.addEventListener('touchend', (e) => e.stopPropagation(), { passive: true });
    for (const btn of this.overlayEl.querySelectorAll<HTMLButtonElement>('[data-buy]')) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const item = SHOP_ITEMS.find((i) => i.id === btn.dataset.buy);
        if (item) this.onShopBuy?.(item);
      });
      btn.addEventListener('touchend', (e) => e.stopPropagation(), { passive: true });
    }
    for (const btn of this.overlayEl.querySelectorAll<HTMLButtonElement>('[data-equip]')) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const item = SHOP_ITEMS.find((i) => i.id === btn.dataset.equip);
        if (item) this.onShopEquip?.(item);
      });
      btn.addEventListener('touchend', (e) => e.stopPropagation(), { passive: true });
    }
  }

  showPaused(): void {
    this.overlayEl.innerHTML = `
      <div class="overlay-card">
        <h1>${this.t('paused')}</h1>
        <p>${this.t('resumeHint')}</p>
      </div>`;
    this.overlayEl.hidden = false;
  }

  showGameOver(stats: {
    score: number;
    distance: number;
    highScore: number;
    isRecord: boolean;
    beltIndex: number;
    challengesCompleted: number;
    bestDistance: number;
    districtBest: number;
    coinsEarned: number;
    wallet: number;
    dailyLabel: string | null;
    qualifiesForBoard: boolean;
  }): void {
    const beltName = beltAt(stats.beltIndex).name;
    this.overlayEl.innerHTML = `
      <div class="overlay-card">
        <h1>${stats.dailyLabel ? `📅 ${stats.dailyLabel}` : this.t('gameOver')}</h1>
        ${stats.isRecord ? `<p class="record">${this.t('newRecord')}</p>` : ''}
        <p>${this.t('score')}: <strong data-countup="${stats.score}">0</strong></p>
        <p>${this.t('distance')}: <strong data-countup="${Math.floor(stats.distance)}">0</strong>m</p>
        <p>${this.t('belt')}: <strong>${beltName}</strong> · ✅ ${stats.challengesCompleted}</p>
        <p>🪙 +${stats.coinsEarned} → ${stats.wallet}</p>
        <p>★ ${stats.highScore} · 🏃 ${Math.floor(stats.bestDistance)}m · 📍 ${stats.districtBest}</p>
        ${
          stats.qualifiesForBoard
            ? `<p class="initials-row">🏆 <input id="initials-input" maxlength="3"
                 placeholder="${this.t('initials')}" autocomplete="off" />
               <button class="menu-btn" id="initials-save">${this.t('save')}</button></p>`
            : ''
        }
        <p class="restart-hint">${this.t('restart')} ⏎</p>
      </div>`;
    this.overlayEl.hidden = false;

    const input = this.overlayEl.querySelector<HTMLInputElement>('#initials-input');
    const saveBtn = this.overlayEl.querySelector<HTMLButtonElement>('#initials-save');
    if (input && saveBtn) {
      for (const el of [input, saveBtn]) {
        el.addEventListener('touchend', (e) => e.stopPropagation(), { passive: true });
        el.addEventListener('click', (e) => e.stopPropagation());
      }
      input.addEventListener('keydown', (e) => e.stopPropagation()); // Don't restart on Enter
      saveBtn.addEventListener('click', () => {
        this.onSubmitInitials?.(input.value);
        input.parentElement?.replaceChildren(document.createTextNode('🏆 ✓'));
      });
    }

    // Count-up animation (v1 game-over screen behavior)
    for (const el of this.overlayEl.querySelectorAll<HTMLElement>('[data-countup]')) {
      const target = Number(el.dataset.countup ?? 0);
      const start = performance.now();
      const tick = (now: number): void => {
        const progress = Math.min((now - start) / 900, 1);
        el.textContent = String(Math.round(target * (1 - Math.pow(1 - progress, 3))));
        if (progress < 1 && el.isConnected) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
  }

  hideOverlay(): void {
    this.overlayEl.hidden = true;
    this.overlayEl.innerHTML = '';
  }
}
